import { ServerEventValidationSchema } from '@thedealersconcierge/lib/cache/events';
import { QueryClient } from '@tanstack/react-query';
import { platformVersionKeys } from './keys/platformVersion';
import { transactionKeys } from './keys/transaction';

const assertNever = (event: never): never => {
  throw new Error(`Unhandled server event: ${JSON.stringify(event)}`);
};

/**
 * Apply a realtime wire event to the consumer cache.
 *
 * For `transaction.updated` the customer only ever receives events for their own
 * transactions (the channel is per-customer), so we invalidate by the
 * transaction's public id — the trailing dealership slug in the key is matched by
 * prefix. `platform.version` is the exception: it carries its value, which we
 * write straight to the cache (no refetch).
 *
 * Invalidation only *refetches* queries that are currently active (have a
 * mounted observer — i.e. on screen): that is React Query's default
 * `refetchType: 'active'`, and we pass no override. So an event for one of the
 * customer's other transactions that is NOT in view never triggers a background
 * fetch — it is a no-op when the query is uncached, or just marks a
 * cached-but-inactive query stale so it refetches the next time it mounts.
 *
 * The `switch` is exhaustive (`assertNever`): adding a variant to the shared
 * schema is a compile error until it is handled here.
 */
export const applyServerEvent = (
  queryClient: QueryClient,
  event: ServerEventValidationSchema
): void => {
  switch (event.type) {
    case 'transaction.updated':
      void queryClient.invalidateQueries({
        queryKey: transactionKeys.all(event.transactionId)
      });
      return;
    case 'platform.version':
      // Not an invalidation: the heartbeat carries the serving backend's version,
      // so we write it straight to the cache (no refetch). The version-mismatch
      // prompt in _app observes this entry; this frame also serves as keep-alive.
      queryClient.setQueryData(
        platformVersionKeys.current(),
        event.platformVersion
      );
      return;
    default:
      return assertNever(event);
  }
};
